import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Chapter, Prose, TheoryBox, MTQuote, Glossary, Term, Steps, Step } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";
import { RhythmPatternPlayer, type RhythmPattern } from "@/components/mt/RhythmPatternPlayer";

/* Four playable patterns that reveal the "container" idea by progressively
   filling the same beat with more events. Same tempo, more events per beat —
   so the body still nods on 1·2·3·4, but the texture above gets busier. */
const QUARTERS: RhythmPattern = {
  name: "Quarters · one event per beat",
  description: "The plainest possible bar — one hit per beat, four beats in the bar. Your head nods exactly with what you hear. This is what 'beat' feels like with no subdivision.",
  bpm: 88,
  cells: ["K", "", "", "", "S", "", "", "", "K", "", "", "", "S", "", "", ""],
};

const DIV_BY_2: RhythmPattern = {
  name: "Divide by 2 · 1 and 2 and 3 and 4 and",
  description: "Each beat is split exactly in half. The downbeats stay strong; the 'ands' sit between them. This is the eighth-note feel.",
  bpm: 88,
  cells: ["K", "", "S", "", "K", "", "S", "", "K", "", "S", "", "K", "", "S", ""],
};

const DIV_BY_4: RhythmPattern = {
  name: "Divide by 4 · 1-e-and-a 2-e-and-a",
  description: "Each beat now holds four events — sixteenth notes. Same tempo, four times the activity. Notice how your body still moves on 1·2·3·4, even though the texture has quadrupled.",
  bpm: 88,
  cells: ["K", "S", "K", "S", "K", "S", "K", "S", "K", "S", "K", "S", "K", "S", "K", "S"],
};

const SWING_SHUFFLE: RhythmPattern = {
  name: "Swing feel · the late 'and'",
  description: "Two events per beat, but the second one lands two-thirds of the way through the beat instead of halfway. The maths is still divide-by-2, but the body now leans rather than marches. This is the swing feel of jazz, blues and shuffle grooves — played here on a triplet grid: each beat is split into three, and the off-beat sits on the third.",
  bpm: 88,
  subdivide: 3,
  cells: ["K", "", "S", "K", "", "S", "K", "", "S", "K", "", "S"],
};

export default function MusicTheory34() {
  const { lesson, course, accent } = findLesson("music-theory", "34");
  return (
    <LessonShell
      course={course}
      courseSlug="music-theory"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/music-theory/MT34-Beat-Divisions-Animated.mp4"
        caption="Halve the beat — straight 50:50 vs swing 66:34"
        hint="≈ 27 sec · watch first"
      />
      <Chapter number="34" eyebrow="Music Theory · Chapter Thirty-Four" title="The beat is a container. The subdivisions are what you put inside it.">
        <Prose>
          <p>
            We've spent thirty-three chapters on what notes and chords <em>are</em>. This
            final chapter is about <em>when</em> they happen. Pitch is one half of music;
            time is the other. And the smallest unit of musical time you'll ever name out
            loud is the <strong>beat</strong>.
          </p>
          <p>
            Most teachers leave it at "the beat is what you tap your foot to," which is
            true but doesn't help you compose anything. So here's the definition I want you
            to carry with you for life:
          </p>
        </Prose>

        <TheoryBox label="The beat, defined">
          A beat is a <strong>time container</strong> for a musical event. The container
          has a fixed duration. What you put <em>inside</em> it — a hit, a rest, a sustain,
          a chord, a melodic flurry — is up to you.
        </TheoryBox>

        <Prose>
          <p>
            Think of those airtight kitchen jars that line a chef's counter. Each jar holds
            the same amount of space; what changes is what's inside. One holds salt, one
            holds pepper, one holds chilli. The size of the jar doesn't change with its
            contents. The beat works the same way. Whether the container is filled with
            silence, a single struck note, a whole chord, or a flurry of sixteen
            sixteenths, the duration is the same — and the listener's body keeps nodding at
            the same rate.
          </p>
        </Prose>
        <NoteImage
          src="/notes/music-theory/finals/34a-beat-division-takadimi.png"
          caption="Jason's note: the three division systems written in plain syllables. ÷2 = 1 & 2 & 3 & 4 & (or 'tha-ka'). ÷3 = 1 &-a 2 &-a 3 &-a 4 &-a (or 'tha-ki-ta'). ÷4 = 1 e & a 2 e & a 3 e & a 4 e & a (or 'tha-ka-dhi-mi'). Same beat container, three ways to divide it."
        />
      </Chapter>

      <Chapter eyebrow="The pulse" title="Why the beat is a body event before it's a maths event">
        <Prose>
          <p>
            Before we touch a single subdivision, notice something almost no theory book
            spends time on: the beat is something the <strong>body</strong> feels, not just
            something the brain counts. Put on any piece of music — a Coldplay ballad, a
            tabla solo, a salsa track, a film score — and watch what happens. Your head
            starts moving. Your foot starts tapping. You didn't decide to. The body finds
            the pulse on its own, and to a remarkable degree, every listener in the room
            finds the <em>same</em> pulse.
          </p>
          <p>
            That involuntary nod is the beat. A composer's job, more than anything else, is
            to feel that pulse from inside the music and then to <em>play with it</em> —
            either honouring it, or stretching it, or surprising it. Without the pulse,
            there's nothing to surprise.
          </p>
        </Prose>

        <MTQuote>
          I'm sure you'll agree that the most important thing for your audience is to move
          to what you're playing, to communicate a sense of movement and get them to just
          move their body, clap the hand, tap the foot or dance.
        </MTQuote>

        <Prose>
          <p>
            Almost every dance form on earth grew up alongside music, and most of them
            organise themselves around a felt pulse. Music has dance to thank for that
            visceral grip. Every rhythm decision you make is, ultimately, a decision about
            how the audience's body will move.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="What you can put in the container" title="Three things — and that's all">
        <Prose>
          <p>
            Once a beat is sitting on the timeline, only three kinds of things can fill
            it. You'll see all three written into every score you ever read.
          </p>
        </Prose>

        <Glossary>
          <Term name="A hit (a note)" def="An event that starts at the beat. Could be one note, a chord, a clap, a drum strike. The classic 1·2·3·4." />
          <Term name="A rest (silence)" def="The container is reserved but holds nothing. Counted, but not played. Silence is a musical event — the negative space that gives hits their weight." />
          <Term name="A sustain (held from earlier)" def="A note that began on a previous beat and is still ringing. A minim engulfs two beats; a semibreve engulfs four. The container is filled by something that started before it." />
        </Glossary>

        <TheoryBox label="The container is fixed; the contents are infinite">
          You can fill four beats with one held semibreve, sixteen sixteenth-note bullets,
          a grand silence, or any combination. The body still nods on 1·2·3·4. That's the
          gift the pulse gives you — a fixed grid you can be as sparse or as dense over as
          you like, and the listener stays oriented either way.
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="The first subdivision" title="Divide by 2 — and the 'and' is born">
        <Prose>
          <p>
            Now we open the container and put a partition through the middle. One beat
            becomes two compartments — equal in size, airtight (the first half doesn't bleed
            into the second). In counting language, this is the moment you stop saying just
            "1·2·3·4" and start saying:
          </p>
        </Prose>

        <TheoryBox label="The eighth-note count">
          <div className="text-center font-mt text-2xl text-amber-200">
            <strong>1 &nbsp;and&nbsp; 2 &nbsp;and&nbsp; 3 &nbsp;and&nbsp; 4 &nbsp;and</strong>
          </div>
          <div className="mt-2 text-center text-[13px] text-nsm-dim">
            Each beat now holds two halves: the downbeat (the number) and the off-beat (the "and").
          </div>
        </TheoryBox>

        <Prose>
          <p>
            How you <em>say</em> the count matters. Whisper the "ands" and the music feels
            relaxed and behind the beat. Punch the "ands" instead — staccato, almost
            shocked — and the body wakes up. The same eight events, reshaped just by where
            you place the energy, become two completely different feels.
          </p>
          <p>
            Listen for that contrast in the player below. The first pattern hits only on
            the four numbers. The second hits on every number <em>and</em> every "and" —
            twice as many events, identical tempo. Watch your head. It nods at the same
            rate either way. That's the pulse staying steady while the texture changes.
          </p>
        </Prose>

        <RhythmPatternPlayer patterns={[QUARTERS, DIV_BY_2]} />

        <Glossary>
          <Term name="Downbeat" def="The numbered counts — 1, 2, 3, 4. The places your foot taps. Where the body feels gravity in the bar." />
          <Term name="Off-beat / up-beat" def="The 'ands' that sit between the downbeats. On a guitar, downbeats are played with a downstroke and up-beats with an upstroke — that's where the names come from." />
        </Glossary>
      </Chapter>

      <Chapter eyebrow="The second subdivision" title="Divide by 3 — the triplet">
        <Prose>
          <p>
            Same beat. Same container. But now we put <em>two</em> partitions in, dividing
            it into three equal compartments instead of two. This is the triplet feel — the
            heartbeat of waltzes, of swing, of countless Bollywood ballads, of every
            6/8 lullaby.
          </p>
          <p>
            The count expands. Most musicians say "<strong>1 and-a 2 and-a 3 and-a 4
            and-a</strong>" or use the Carnatic syllables "<strong>ta-ki-ta</strong>" — but
            the move is the same: three events in the time one event used to take.
          </p>
        </Prose>

        <TheoryBox label="The triplet count">
          <div className="text-center font-mt text-2xl text-amber-200">
            <strong>1 &nbsp;trip-let &nbsp;·&nbsp; 2 &nbsp;trip-let &nbsp;·&nbsp; 3 &nbsp;trip-let &nbsp;·&nbsp; 4 &nbsp;trip-let</strong>
          </div>
          <div className="mt-2 text-center text-[13px] text-nsm-dim">
            Three syllables per beat, equal spacing. The pulse still falls on the numbers — your head shouldn't change.
          </div>
        </TheoryBox>

        <Prose>
          <p>
            Triplets are seasoning, not a staple. Drop one or two into a four-on-the-floor
            groove and the listener feels a sudden lift, like the music briefly took a
            breath. Use them all the time and the music starts to feel like a waltz, which
            is its own world. Knowing when to deploy them — and when to leave them out — is
            half of what makes a good drummer feel "musical."
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="The third subdivision" title="Divide by 4 — sixteenth notes">
        <Prose>
          <p>
            Three partitions now. The container splits into four equal compartments, and
            the count adds one more layer:
          </p>
        </Prose>

        <TheoryBox label="The sixteenth-note count">
          <div className="text-center font-mt text-2xl text-amber-200">
            <strong>1 &nbsp;e &nbsp;and &nbsp;a &nbsp;·&nbsp; 2 &nbsp;e &nbsp;and &nbsp;a &nbsp;·&nbsp; 3 &nbsp;e &nbsp;and &nbsp;a &nbsp;·&nbsp; 4 &nbsp;e &nbsp;and &nbsp;a</strong>
          </div>
          <div className="mt-2 text-center text-[13px] text-nsm-dim">
            Sixteen events per bar. The Carnatic equivalent is "ta-ka-di-mi" — same idea, different language.
          </div>
        </TheoryBox>

        <Prose>
          <p>
            Sixteenths are where modern groove music lives — funk, R&B, drum-and-bass,
            half the chart pop you've ever heard. Try the player below. Same tempo as
            before, but now sixteen events per bar instead of four. Notice the
            "shock-into-action" feeling on the e's and a's that the eighth-note pattern
            didn't have. That's the body responding to a finer grid.
          </p>
        </Prose>

        <RhythmPatternPlayer patterns={[DIV_BY_2, DIV_BY_4]} />

        <Prose>
          <p>
            The trick is that <em>your head still nods on 1·2·3·4</em>. The pulse doesn't
            change. The grid above the pulse just got finer. That's the whole secret of
            rhythm: a steady pulse below, an arbitrary number of events painted on top.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="Same maths, different mood" title="Straight vs swing — moving the 'and'">
        <Prose>
          <p>
            Here's a beautiful piece of theatre that the maths doesn't capture cleanly.
            Take divide-by-2 — one "and" per beat, sitting at exactly the 50% mark. That's
            "<strong>straight</strong>" feel. The off-beat lands precisely halfway between
            the numbers. Pop, rock, EDM, classical — almost all of it is straight.
          </p>
          <p>
            Now slide that "and" later. Move it from the 50% mark to roughly 67% — two
            thirds of the way through the beat. Same number of events, same downbeats. But
            the off-beat is now late. The body stops marching and starts <em>leaning</em>.
            Welcome to <strong>swing</strong>.
          </p>
        </Prose>

        <Steps>
          <Step><strong>Straight.</strong> Off-beat at 50%. The "and" sits exactly between the numbers. Body marches. Pop and rock default.</Step>
          <Step><strong>Light swing.</strong> Off-beat slid to ~60%. Subtle lilt. A lot of pop ballads quietly do this and you don't notice.</Step>
          <Step><strong>Hard swing.</strong> Off-beat at ~67% (two-thirds). The off-beat is closer to the next downbeat than the previous one. Jazz and blues live here.</Step>
          <Step><strong>Shuffle.</strong> Same swing maths, often written as a triplet with the middle note silent. A blues "ba-da, ba-da, ba-da" feel.</Step>
        </Steps>

        <Prose>
          <p>
            The pattern below plays real triplet swing — each beat split into three, the
            off-beat on the third. Compare it back-to-back with the straight divide-by-2
            to feel the difference. The notes are the same. The maths beneath them isn't.
          </p>
        </Prose>

        <RhythmPatternPlayer patterns={[DIV_BY_2, SWING_SHUFFLE]} />

        <TheoryBox label="The swing rule">
          Straight = off-beat at 50%. Swing = off-beat slid later, typically toward 67%
          (two-thirds of the beat). Same count, same tempo, opposite mood. If a piece is
          marked "swing", you treat written eighth-notes as a long-short triplet figure
          rather than two equal halves.
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="Reading and feeling, together" title="From maths to body, and back">
        <Prose>
          <p>
            One last reframing, because this is the chapter that ties theory to
            performance. When you read a score, the symbols carry their division
            information openly:
          </p>
        </Prose>

        <Glossary>
          <Term name="Crotchet (quarter note)" def="One full beat. The basic container." />
          <Term name="Quaver (eighth note)" def="Half a beat. Divide by 2." />
          <Term name="Triplet quaver" def="A third of a beat. Divide by 3." />
          <Term name="Semiquaver (sixteenth note)" def="A quarter of a beat. Divide by 4." />
          <Term name="Minim (half note)" def="Two beats sustained — one note engulfing two containers." />
          <Term name="Semibreve (whole note)" def="Four beats sustained. Common as a dramatic chord that rings under a melody." />
        </Glossary>

        <Prose>
          <p>
            The musician's job is to translate those symbols into <em>body feel</em>. A
            page of sixteenths isn't a maths problem to be solved correctly. It's an
            instruction to put your body into the takadimi state — quick, shock-jumpy,
            alive. A page of triplets is an instruction to lean and lilt. A swing mark is
            an instruction to push the second half of every beat late.
          </p>
          <p>
            And the same goes the other way. When you compose, you don't pick "I'll write
            sixteenths now" and then hope the body responds. You feel the body state you
            want — march, lean, sprint, breathe — and write the subdivision that
            <em> produces</em> that state.
          </p>
        </Prose>

        <MTQuote>
          When you're giving this out to the audience, you need to feel the pulse from
          within your body. You need to feel the subdivisions from within your body, and
          feel the subdivisions differently, and know how the beats are being divided.
        </MTQuote>
      </Chapter>

      <Chapter eyebrow="Closing the course" title="What thirty-four chapters were really about">
        <Prose>
          <p>
            We started with twelve notes drawn around a clock and the simple TTSTTTS recipe
            of the major scale. From that one rule, every chapter that followed was a
            consequence — scale degrees, modes, the circle of fifths, the chord tree, key
            signatures, secondary dominants, jazz extensions, time signatures, and finally
            the beat itself.
          </p>
          <p>
            The story theory tells, in the end, is a generous one: a small number of rules,
            applied with care, generates an enormous amount of music. You don't need to
            memorise every chord in every key. You need the recipe and the patience to
            derive what you need on the spot. That's what theory <em>is</em>: not a wall of
            facts, but a small set of rules you can run forward to recreate any of the
            music you love — and to make music nobody has heard before.
          </p>
          <p>
            From here, the work is no longer "learning theory." The work is using it. Take
            your favourite song, sit at the piano, and ask: what scale, what tree, what
            beat, what subdivision? Within an afternoon, you'll have answered for music
            that used to feel mysterious. Within a year, you'll be writing your own.
          </p>
        </Prose>

        <TheoryBox label="The whole course on a postcard">
          Twelve notes. Pick seven of them with TTSTTTS — that's a key. Stack thirds inside
          that key — that's the chord tree. Number the chords I, ii, iii, IV, V, vi, vii° —
          that's roman numeral analysis. Pulse + subdivision + groove — that's rhythm.
          Everything else is colour: borrowed chords, secondary dominants, modal
          interchange, jazz extensions. The rules are small. The music is endless.
        </TheoryBox>

        <Prose>
          <p className="text-center text-amber-300">
            <strong>That's the course. Now go and write something.</strong>
          </p>
        </Prose>
        <NoteImage
          src="/notes/music-theory/finals/34c-straight-vs-swing-66-percent.png"
          caption="Straight vs Swing — straight off-beats land at 50% (exactly halfway between numbers); swing pushes them to ≈67% (two-thirds of the way through the beat). The maths is in fact a triplet eighth: each beat gets divided into 3, and the off-beat occupies the 2nd-and-3rd-thirds. That's why 'shuffle' grooves notate as triplet eighths."
        />
      </Chapter>
    </LessonShell>
  );
}
